/* Live shared overlay — what students (and teacher) see when something is "live-shared".
 *
 * Renders the right viewer based on overlay.type:
 *   - "chapter"  → big slide-deck of chapter pages
 *   - "quiz"     → interactive quiz player (students submit answers, teacher sees live results)
 *   - "story"    → animated story carousel (uses AnimatedStoryOverlay inline)
 *   - "resource" → embedded library asset (pdf/image)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { correctIndexOf, lenientMatch } from "@/lib/quizUtils";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  X, ChevronLeft, ChevronRight, BookOpen, FileText, Sparkles, CheckCircle2,
  Timer, Trophy, ExternalLink, Hand, Loader2, Users, Send, Crown, ChevronDown, MousePointer2,
  Bell, BellOff,
} from "lucide-react";
import { useAnswerChime, useChimeMute } from "@/lib/answerChime";
import { ChampStudentPlayer, ChampMirrorStage } from "@/components/game/MillionaireGame";
import { SlidesPresenter, themeFor } from "@/components/slides/SlidesBody";
import ShareTargetModal from "@/components/live/ShareTargetModal";
import { AnnotationLayer } from "@/components/live/AnnotationLayer";

/**
 * Props:
 *   overlay: { id, type, ref_id, payload, allowed_ids, allowed_names, mode, opened_at }
 *   mode "remote" → ONE shared mirrored session; allowed participants (+ teacher) control it.
 *   mode "independent" → everyone attempts on their own screen.
 */
export default function LiveSharedOverlay({ overlay, classId, isTeacher, onDismiss, guestToken, guestName, myParticipantId, myName, onStudentClose }) {
  const [controlPicker, setControlPicker] = useState(false);
  if (!overlay) return null;
  const allowed = overlay.allowed_ids;
  const isRemote = overlay.mode === "remote";
  const canInteract = isTeacher || !allowed || allowed.length === 0 || allowed.includes("*") || (!!myParticipantId && allowed.includes(myParticipantId));
  const allowedNames = overlay.allowed_names || [];
  // Millionaire + slides + story fill the screen (presentation modes)
  const fullBleed = overlay.type === "millionaire" || overlay.type === "slides" || overlay.type === "story";
  // Teacher can draw over presentational overlays; strokes sync to students.
  const annotatable = ["story", "slides", "chapter", "resource"].includes(overlay.type);

  // Teacher presenting slides — full PPT-style toolbox (themes, images,
  // shapes, AI edit) with navigation + edits broadcast live to students.
  if (overlay.type === "slides" && isTeacher) {
    return (
      <>
        <TeacherSlidesSync key={overlay.id} classId={classId} overlay={overlay} onDismiss={onDismiss} />
        <div className="fixed inset-0 z-[92] pointer-events-none">
          <AnnotationLayer
            strokes={overlay.payload?.annotations || []}
            editable
            onCommit={(st) => api.post(`/live/${classId}/overlay-update`, { payload: { annotations: st } }).catch(() => {})}
          />
        </div>
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-[70] bg-[#0A0C10]/95 backdrop-blur-md" data-testid="live-shared-overlay">
      <div className="absolute top-4 left-4 z-10 flex items-center gap-3 flex-wrap max-w-[75vw]">
        <div className="px-3 py-1.5 bg-[#FFD600] text-[#0A0C10] text-[11px] font-mono uppercase tracking-[0.22em] font-bold rounded-sm inline-flex items-center gap-1.5">
          <Sparkles size={12} /> Live · {overlay.type}
        </div>
        <div className="text-white text-sm font-medium truncate max-w-md">{overlay.payload?.title || "—"}</div>
        {isRemote && (
          <div className="px-2.5 py-1 rounded-full bg-[#22D3EE]/15 border border-[#22D3EE]/40 text-[#22D3EE] text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1.5" data-testid="remote-badge">
            <MousePointer2 size={10} /> Remote · {allowedNames.join(", ") || "selected"}
            {isTeacher && (
              <button onClick={(e) => { e.stopPropagation(); setControlPicker(true); }} data-testid="remote-change-controller" className="underline font-bold hover:text-white relative z-20 px-1 py-0.5">
                change
              </button>
            )}
          </div>
        )}
      </div>
      {isTeacher && (
        <button
          onClick={onDismiss}
          data-testid="dismiss-overlay-btn"
          className="absolute top-4 right-4 z-10 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm font-medium inline-flex items-center gap-2 transition-colors"
        ><X size={14} /> End share</button>
      )}
      <div className={`h-full w-full ${fullBleed ? "pt-14 pb-2 px-2" : "pt-16 pb-6 px-4"} overflow-y-auto flex items-start justify-center`}>
        <div className={`w-full ${fullBleed ? "max-w-[1700px]" : "max-w-6xl"}`}>
          {overlay.type === "chapter" && <ChapterViewer payload={overlay.payload} />}
          {overlay.type === "story" && <StoryViewer payload={overlay.payload} storyId={overlay.ref_id || overlay.payload?.id} isTeacher={isTeacher} classId={classId} />}
          {overlay.type === "quiz" && (isRemote ? (
            <SharedQuizSession
              overlay={overlay}
              classId={classId}
              isTeacher={isTeacher}
              canControl={canInteract}
              allowedNames={allowedNames}
              guestToken={guestToken}
              guestName={guestName}
              myName={myName}
              onStudentClose={onStudentClose}
            />
          ) : (
            <QuizViewer
              classId={classId}
              quizId={overlay.ref_id}
              payload={overlay.payload}
              isTeacher={isTeacher}
              guestToken={guestToken}
              guestName={guestName}
              onStudentClose={onStudentClose}
            />
          ))}
          {overlay.type === "millionaire" && (
            <MillionaireViewer classId={classId} gameId={overlay.ref_id} payload={overlay.payload} isTeacher={isTeacher} guestToken={guestToken} guestName={guestName} isRemote={isRemote} canInteract={canInteract} allowedNames={allowedNames} myName={myName} onStudentClose={onStudentClose} />
          )}
          {overlay.type === "slides" && (
            <SlidesViewer payload={overlay.payload} canControl={isRemote && canInteract} classId={classId} guestToken={guestToken} guestName={guestName || myName} />
          )}
          {overlay.type === "resource" && <ResourceViewer payload={overlay.payload} />}
          {overlay.type === "recording" && <RecordingViewer payload={overlay.payload} />}
        </div>
      </div>

      {/* Pen / pointer annotations — teacher draws, everyone sees */}
      {annotatable && (
        <AnnotationLayer
          strokes={overlay.payload?.annotations || []}
          editable={isTeacher}
          onCommit={(st) => api.post(`/live/${classId}/overlay-update`, { payload: { annotations: st } }).catch(() => {})}
        />
      )}

      {/* Teacher hands remote control to someone else (or back to Everyone) */}
      {isTeacher && isRemote && (
        <ShareTargetModal
          open={controlPicker}
          classId={classId}
          title="Remote access control"
          subtitle="Everyone keeps watching the same shared screen — pick who drives it. You always keep control too."
          confirmLabel="Give control"
          everyoneDesc="Switch to independent mode — each participant attempts on their own"
          specificDesc="These participants drive the shared screen"
          onClose={() => setControlPicker(false)}
          onConfirm={async (ids, names) => {
            try {
              await api.post(`/live/${classId}/overlay-update`, { allowed_ids: ids, allowed_names: names });
              setControlPicker(false);
              toast.success(ids.includes("*") ? "Switched to independent mode" : `Control handed to ${names.join(", ")}`);
            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }}
        />
      )}
    </div>
  );
}

/* ───────────────────── teacher slides presenter (live-synced) ───────────
   Wraps the dashboard SlidesPresenter (theme swap, insert image, shapes, AI
   prompt-edit) and broadcasts every navigation + edit to students via
   /live/{id}/overlay-update — same overlay id, merged payload. */
function TeacherSlidesSync({ classId, overlay, onDismiss }) {
  const [deck, setDeck] = useState(() => ({
    id: overlay.ref_id,
    title: overlay.payload?.title || "Slides",
    theme: overlay.payload?.theme || "indigo",
    slides: overlay.payload?.slides || [],
  }));
  const push = (patch) => api.post(`/live/${classId}/overlay-update`, { payload: patch }).catch(() => {});
  return (
    <SlidesPresenter
      deck={deck}
      onClose={onDismiss}
      onUpdated={(d) => { setDeck(d); push({ title: d.title, theme: d.theme, slides: d.slides }); }}
      onIndexChange={(i) => push({ current_index: i })}
      syncIndex={overlay.payload?.current_index}
    />
  );
}

/* ───────────────────── shared quiz session (remote access) ─────────────
   ONE mirrored quiz instance for the whole room. The controller(s) (chosen
   participants + the teacher) drive it; every action POSTs session-state and
   all viewers converge on the next poll. */
function SharedQuizSession({ overlay, classId, isTeacher, canControl, allowedNames, guestToken, guestName, myName, onStudentClose }) {
  const payload = overlay.payload || {};
  const { questions = [], category = "mcq", title = "Quiz" } = payload;
  const session = payload.session || {};
  const total = questions.length;
  const [local, setLocal] = useState({ current_q: 0, answers: {}, submitted: false });
  const lastActionRef = useRef(0);

  // Adopt the server session unless WE just acted (avoid clobbering our own
  // optimistic state before the poll echoes it back).
  const sessionKey = JSON.stringify(session);
  useEffect(() => {
    if (Date.now() - lastActionRef.current < 3000) return;
    setLocal({
      current_q: session.current_q || 0,
      answers: session.answers || {},
      submitted: !!session.submitted,
      score: session.score,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  const push = (next) => {
    lastActionRef.current = Date.now();
    setLocal(next);
    const body = { payload: { session: next } };
    (guestToken
      ? api.post(`/public/class/${classId}/session-state`, { token: guestToken, guest_name: guestName || myName || "Guest", ...body })
      : api.post(`/live/${classId}/session-state`, body)
    ).catch(() => {});
  };

  const idx = total ? Math.max(0, Math.min(local.current_q || 0, total - 1)) : 0;
  const q = questions[idx];
  const answers = local.answers || {};
  const setAnswer = (v) => { if (!canControl || local.submitted) return; push({ ...local, answers: { ...answers, [idx]: v } }); };
  const nav = (n) => { if (!canControl || local.submitted) return; push({ ...local, current_q: n }); };
  const submit = () => {
    if (!canControl || local.submitted) return;
    const sc = scoreFn(category, questions, answers);
    push({ ...local, submitted: true, score: sc, submitted_by: myName || (isTeacher ? "Teacher" : "Class") });
    toast.success(`Submitted · ${sc} / ${total}`);
  };

  if (!q) return <EmptyShell icon={FileText} msg="Empty quiz." />;

  if (local.submitted) {
    const sc = local.score ?? scoreFn(category, questions, answers);
    const pct = total ? Math.round((sc / total) * 100) : 0;
    return (
      <div className="text-center py-20 max-w-2xl mx-auto" data-testid="shared-quiz-done">
        <div className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FF7AD9] items-center justify-center mb-5">
          <Trophy size={36} className="text-white" />
        </div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/60">/ solved together · remote access</div>
        <h2 className="font-display font-bold text-5xl text-white tracking-tighter mt-2">{sc} / {total}</h2>
        <p className="text-white/70 mt-2">{pct >= 80 ? "Outstanding teamwork! 🎉" : "Great effort — reviewed live by everyone."}</p>
        {!isTeacher && onStudentClose && (
          <button onClick={onStudentClose} data-testid="student-quiz-close"
            className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-white text-[#0A0C10] text-sm font-bold hover:bg-neutral-100 transition-colors">
            <X size={14} /> Close
          </button>
        )}
        <QuizReview questions={questions} category={category} answers={answers} />
      </div>
    );
  }

  return (
    <div data-testid="shared-quiz-session">
      <div className="flex items-center justify-between mb-4 max-w-4xl mx-auto flex-wrap gap-2">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/50">
          / remote access · {category} · {idx + 1} of {total}
        </div>
        {canControl ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-400 text-[10px] font-mono uppercase tracking-wider" data-testid="shared-quiz-control-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> You have control
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-wider" data-testid="shared-quiz-watch-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" /> {allowedNames.join(", ") || "Controller"} is solving — live
          </span>
        )}
      </div>
      <div className="h-1 w-full max-w-4xl mx-auto bg-white/10 rounded mb-6 overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-[#22D3EE] via-[#FF7AD9] to-[#7C3AED]"
          initial={{ width: 0 }} animate={{ width: `${((idx + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>
      <div className="max-w-4xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2 }}
            className={canControl ? "" : "pointer-events-none"}>
            {qKind(q, category) === "mcq" && <InlineMCQ q={q} value={answers[idx]} onChange={setAnswer} />}
            {qKind(q, category) === "drag_drop" && <InlineDragDrop q={q} value={answers[idx]} onChange={setAnswer} />}
            {qKind(q, category) === "fill_blank" && <InlineFillBlank q={q} value={answers[idx]} onChange={setAnswer} />}
          </motion.div>
        </AnimatePresence>
        {canControl ? (
          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => idx > 0 && nav(idx - 1)} disabled={idx === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm disabled:opacity-30"
              data-testid="shared-q-prev"
            ><ChevronLeft size={14} /> Previous</button>
            {idx < total - 1 ? (
              <button onClick={() => nav(idx + 1)} data-testid="shared-q-next"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-[#0A0C10] text-sm font-semibold hover:scale-[1.02] transition-transform"
              >Next <ChevronRight size={14} /></button>
            ) : (
              <button onClick={submit} data-testid="shared-q-finish"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-bold hover:bg-[#FFC600] transition-colors"
              >Submit answers <Send size={14} /></button>
            )}
          </div>
        ) : (
          <div className="mt-8 text-center text-[11px] font-mono uppercase tracking-[0.18em] text-white/40">
            Question follows the controller — selections appear live
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────── recording viewer (Phase 2 sync playback) ─────────
   Renders a <video> that snaps to the teacher's current_time / playing
   state every time the overlay payload updates. Drift threshold: 1.5s
   (large enough to avoid stuttering on tiny network jitter, small enough
   to keep everyone in sync). */
function RecordingViewer({ payload }) {
  const vidRef = React.useRef(null);
  const { recording_url = "", current_time = 0, playing = false, title = "" } = payload || {};
  const src = recording_url.startsWith("http") ? recording_url : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${recording_url}`;

  React.useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    // Snap to teacher's currentTime if we've drifted more than 1.5s
    try {
      if (Math.abs(v.currentTime - current_time) > 1.5) {
        v.currentTime = current_time;
      }
      if (playing && v.paused) {
        v.play().catch(() => { /* autoplay may need a user gesture */ });
      } else if (!playing && !v.paused) {
        v.pause();
      }
    } catch { /* noop */ }
  }, [current_time, playing, src]);

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="text-white font-display font-semibold text-lg mb-3 text-center">{title || "Recording"}</div>
      <video
        ref={vidRef}
        src={src}
        controls
        className="w-full bg-black rounded-lg"
        data-testid="student-recording-video"
      />
      <div className="text-center text-xs text-neutral-400 mt-2 font-mono uppercase tracking-[0.18em]">
        Playback controlled by teacher — you can only watch
      </div>
    </div>
  );
}

/* ───────────────────── chapter viewer ───────────────────── */
function pageUrl(u) {
  if (!u) return "";
  if (u.startsWith("data:") || u.startsWith("http")) return u;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api${u}`;
}
function ChapterViewer({ payload }) {
  const pages = payload?.pages || [];
  const [idx, setIdx] = useState(0);
  if (pages.length === 0) {
    return <EmptyShell icon={BookOpen} msg="No pages in this chapter." />;
  }
  const p = pages[idx];
  return (
    <div data-testid="live-chapter-viewer">
      <div className="text-center mb-4 text-[11px] font-mono uppercase tracking-[0.28em] text-white/50">
        Page {idx + 1} / {pages.length}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="bg-white rounded-md overflow-hidden shadow-2xl mx-auto max-w-3xl"
        >
          <img src={pageUrl(p.image_url)} alt={p.summary} className="w-full h-auto" />
          {p.summary && (
            <div className="p-4 border-t border-neutral-100">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0EA5E9] font-bold">Summary</div>
              <p className="text-sm text-[#0A0C10] mt-1">{p.summary}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      <div className="mt-6 flex items-center justify-between max-w-3xl mx-auto">
        <button
          onClick={() => idx > 0 && setIdx(idx - 1)}
          disabled={idx === 0}
          data-testid="live-page-prev"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm disabled:opacity-30 transition-colors"
        ><ChevronLeft size={14} /> Previous</button>
        <button
          onClick={() => idx < pages.length - 1 && setIdx(idx + 1)}
          disabled={idx >= pages.length - 1}
          data-testid="live-page-next"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-white text-[#0A0C10] text-sm font-semibold disabled:opacity-30 transition-colors"
        >Next <ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

/* ───────────────────── story viewer ─────────────────────
   Teacher-driven: navigation syncs to students via payload.current_index
   (students have NO next/prev). Illustrations generate SEQUENTIALLY starting
   with slide 1 — shown the moment each is ready, with a live progress chip. */
function StoryViewer({ payload, storyId, isTeacher, classId }) {
  const slides = payload?.slides || [];
  const serverIdx = Math.max(0, Math.min(payload?.current_index || 0, Math.max(0, slides.length - 1)));
  const [localIdx, setLocalIdx] = useState(null);
  const lastNavRef = useRef(0);
  const idx = (isTeacher && localIdx != null && Date.now() - lastNavRef.current < 3000) ? localIdx : serverIdx;
  const [slideImages, setSlideImages] = useState({}); // { idx: url }
  const [progress, setProgress] = useState(null); // { done, total }
  const [promptOpen, setPromptOpen] = useState(false);
  const [imgPrompt, setImgPrompt] = useState("");
  const [promptBusy, setPromptBusy] = useState(false);
  const startedRef = useRef(false);

  const nav = (n) => {
    if (!isTeacher) return;
    const c = Math.max(0, Math.min(n, slides.length - 1));
    lastNavRef.current = Date.now();
    setLocalIdx(c);
    api.post(`/live/${classId}/overlay-update`, { payload: { current_index: c } }).catch(() => {});
  };

  // TEACHER — generate ALL missing illustrations one by one, slide 1 first.
  // Each image appears the moment it's ready while the rest keep cooking.
  useEffect(() => {
    if (!storyId || !slides.length || !isTeacher || startedRef.current) return;
    startedRef.current = true;
    let stop = false;
    (async () => {
      const missing = slides.map((s, i) => ({ s, i })).filter(({ s }) => !s.image_url);
      if (!missing.length) return;
      let done = slides.length - missing.length;
      setProgress({ done, total: slides.length });
      for (const { s, i } of missing) {
        if (stop) return;
        try {
          const { data } = await api.post("/ai/story/slide-image", {
            prompt: s.image_prompt || s.title, story_id: storyId, slide_index: s.slide_index ?? i,
          });
          if (data.image_url && !stop) setSlideImages((m) => ({ ...m, [i]: data.image_url }));
        } catch { /* placeholder stays */ }
        done += 1;
        if (!stop) setProgress({ done, total: slides.length });
      }
      if (!stop) setProgress(null);
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId, slides.length, isTeacher]);

  // STUDENT — poll the free cached endpoint for the slide being shown.
  useEffect(() => {
    if (!storyId || !slides.length || isTeacher) return;
    const sl = slides[idx];
    if (!sl || sl.image_url || slideImages[idx]) return;
    let stop = false;
    (async () => {
      for (let a = 0; a < 30 && !stop; a++) {
        try {
          const { data } = await api.get(`/ai/story/${storyId}/slide-image-cached`, { params: { slide_index: sl.slide_index ?? idx } });
          if (data.image_url) { if (!stop) setSlideImages((m) => ({ ...m, [idx]: data.image_url })); return; }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 3000));
      }
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, storyId, isTeacher, slides.length]);

  const regenFromPrompt = async () => {
    const p = imgPrompt.trim();
    if (!p) return;
    setPromptBusy(true);
    try {
      const sl = slides[idx];
      const { data } = await api.post("/ai/story/slide-image", {
        prompt: p, story_id: storyId, slide_index: sl.slide_index ?? idx, force: true,
      });
      if (data.image_url) {
        setSlideImages((m) => ({ ...m, [idx]: data.image_url }));
        setPromptOpen(false); setImgPrompt("");
        toast.success("Image generated");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Image generation failed"); }
    finally { setPromptBusy(false); }
  };

  if (slides.length === 0) return <EmptyShell icon={BookOpen} msg="No slides in this story." />;
  const sl = slides[idx];
  const currentImage = slideImages[idx] || sl.image_url;
  const pct = progress ? Math.round((progress.done / progress.total) * 100) : 100;
  return (
    <div className="w-full max-w-[1500px] mx-auto" data-testid="live-story-viewer">
      <div className="flex items-center justify-center gap-4 mb-4">
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#22D3EE] font-bold">
          Scene {idx + 1} / {slides.length}
        </div>
        {isTeacher && progress && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10" data-testid="story-image-progress">
            <Loader2 size={11} className="animate-spin text-[#22D3EE]" />
            <span className="text-[10px] font-mono text-white/70">Illustrations {progress.done}/{progress.total} · {pct}%</span>
            <span className="w-20 h-1 rounded bg-white/10 overflow-hidden"><span className="block h-full bg-[#22D3EE] transition-all" style={{ width: `${pct}%` }} /></span>
            <span className="text-[10px] font-mono text-white/40">{progress.total - progress.done} left</span>
          </div>
        )}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.3 }}
          className="grid lg:grid-cols-[1.35fr_1fr] gap-6 lg:gap-10 items-center bg-[#0D1117] border border-white/10 rounded-2xl p-5 lg:p-8 w-full"
        >
          <div className="relative rounded-xl overflow-hidden bg-black/40 aspect-[4/3] border border-white/10">
            {currentImage ? (
              <img src={currentImage} alt={sl.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white/40">
                <Loader2 size={30} className="animate-spin text-[#22D3EE] mb-2" />
                <div className="text-[10px] font-mono uppercase tracking-wider">Drawing scene…</div>
              </div>
            )}
            {isTeacher && (
              <button
                onClick={() => { setPromptOpen((v) => !v); setImgPrompt(""); }}
                data-testid="story-image-prompt-btn"
                className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#0A0C10]/85 border border-[#22D3EE]/40 text-[#22D3EE] text-[11px] font-semibold hover:bg-[#22D3EE]/15 transition-colors"
              ><Sparkles size={11} /> New image from prompt</button>
            )}
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#22D3EE] font-bold">Slide {idx + 1}</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter text-white leading-tight mt-2">{sl.title}</h2>
            <ul className="mt-5 space-y-2.5">
              {(sl.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-white/85 text-base leading-relaxed">
                  <Sparkles size={12} className="text-[#22D3EE] mt-1.5 shrink-0" /> <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </AnimatePresence>
      {isTeacher && promptOpen && (
        <div className="mt-3 flex items-center gap-2 max-w-2xl mx-auto" data-testid="story-image-prompt-bar">
          <input
            autoFocus
            value={imgPrompt}
            onChange={(e) => setImgPrompt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !promptBusy && regenFromPrompt()}
            placeholder="Describe the image you want for this slide…"
            data-testid="story-image-prompt-input"
            className="flex-1 px-3 py-2.5 rounded-md bg-[#0D1117] border border-white/15 text-white text-sm outline-none focus:border-[#22D3EE] placeholder:text-white/30"
          />
          <button
            onClick={regenFromPrompt} disabled={promptBusy || !imgPrompt.trim()}
            data-testid="story-image-prompt-go"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#22D3EE] text-[#0A0C10] text-sm font-bold disabled:opacity-40"
          >{promptBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate</button>
        </div>
      )}
      <div className="mt-5 flex items-center justify-between w-full">
        {isTeacher ? (
          <>
            <button
              onClick={() => nav(idx - 1)} disabled={idx === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm disabled:opacity-30"
              data-testid="live-story-prev"
            ><ChevronLeft size={14} /> Previous</button>
            <span className="inline-flex items-center gap-2 text-[10px] text-emerald-400 font-mono uppercase tracking-[0.18em]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Students follow your navigation
            </span>
            <button
              onClick={() => nav(idx + 1)} disabled={idx >= slides.length - 1}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#22D3EE] text-[#0A0C10] text-sm font-semibold disabled:opacity-30"
              data-testid="live-story-next"
            >Next <ChevronRight size={14} /></button>
          </>
        ) : (
          <span className="mx-auto inline-flex items-center gap-2 text-xs text-white/50 font-mono uppercase tracking-[0.18em]" data-testid="story-follow-hint">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Teacher is presenting — the story follows automatically
          </span>
        )}
      </div>
    </div>
  );
}

/* ───────────────────── quiz viewer ───────────────────── */
function QuizViewer({ classId, quizId, payload, isTeacher, guestToken, guestName, onStudentClose }) {
  const { questions = [], category = "mcq", title = "Quiz" } = payload || {};

  // STUDENT — independent attempt on their own screen
  if (!isTeacher) {
    return <StudentQuizSession classId={classId} quizId={quizId} title={title} category={category} questions={questions} guestToken={guestToken} guestName={guestName} onStudentClose={onStudentClose} />;
  }
  // TEACHER — live responses dashboard
  return <TeacherLiveResults classId={classId} quizId={quizId} title={title} category={category} total={questions.length} questions={questions} />;
}

function StudentQuizSession({ classId, quizId, title, category, questions, guestToken, guestName, onStudentClose }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [timer, setTimer] = useState(0);
  const total = questions.length;
  const q = questions[idx];

  // Post quiz responses via the guest public endpoint when we're on a guest
  // invite link, otherwise via the authenticated /live/{id}/quiz-response.
  const postQuizResponse = (body) => {
    if (guestToken) {
      return api.post(`/public/class/${classId}/quiz-response`, {
        ...body, token: guestToken, guest_name: guestName || "Guest",
      });
    }
    return api.post(`/live/${classId}/quiz-response`, body);
  };

  useEffect(() => { const t = setInterval(() => setTimer((s) => s + 1), 1000); return () => clearInterval(t); }, []);

  // Auto-submit partial progress every 4 seconds while taking the quiz
  useEffect(() => {
    if (submitted) return;
    const stamp = setTimeout(() => {
      const sc = scoreFn(category, questions, answers);
      postQuizResponse({
        quiz_id: quizId, answers, score: sc, total, time_s: timer, current_q: idx, status: "partial",
      }).catch(() => {});
    }, 4000);
    return () => clearTimeout(stamp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, idx, submitted, classId, quizId, category, questions, total, timer]);

  const finish = async () => {
    if (submitted) return;
    const sc = scoreFn(category, questions, answers);
    setSubmitted(true);
    try {
      await postQuizResponse({
        quiz_id: quizId, answers, score: sc, total, time_s: timer, current_q: total - 1, status: "final",
      });
      // Best-effort: try to earn badges (skip for guests — no user account)
      if (!guestToken) {
        api.post("/badges/award", {
          quiz_id: quizId, score: sc, total, context: "live",
        }).then((r) => {
          const earned = r?.data?.earned || [];
          for (const b of earned) toast.success(`🏆 Badge earned: ${b.title}`);
        }).catch(() => {});
      }
      toast.success(`Submitted · ${sc} / ${total}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    }
  };

  if (submitted) {
    const sc = scoreFn(category, questions, answers);
    const pct = total ? Math.round((sc / total) * 100) : 0;
    const great = pct >= 80;
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="text-center py-20 max-w-2xl mx-auto"
        data-testid="student-quiz-done"
        ref={(el) => {
          if (el && great) {
            // celebrate
            const end = Date.now() + 1500;
            const fire = () => {
              confetti({ particleCount: 50, angle: 60, spread: 70, origin: { x: 0, y: 0.8 }, colors: ["#FFD600", "#FF7AD9", "#7C3AED"] });
              confetti({ particleCount: 50, angle: 120, spread: 70, origin: { x: 1, y: 0.8 }, colors: ["#FFD600", "#10B981", "#0EA5E9"] });
              if (Date.now() < end) requestAnimationFrame(fire);
            };
            fire();
          }
        }}
      >
        <div className="inline-flex w-20 h-20 rounded-full bg-gradient-to-br from-[#FFD600] to-[#FF7AD9] items-center justify-center mb-5">
          <Trophy size={36} className="text-white" />
        </div>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/60">/ submitted</div>
        <h2 className="font-display font-bold text-5xl text-white tracking-tighter mt-2">{sc} / {total}</h2>
        <p className="text-white/70 mt-2">{great ? "Outstanding! 🎉" : "Great effort — keep practising."}</p>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/40 mt-1">Your teacher sees your answers now.</p>
        {onStudentClose && (
          <button
            onClick={onStudentClose}
            data-testid="student-quiz-close"
            className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-white text-[#0A0C10] text-sm font-bold hover:bg-neutral-100 transition-colors"
          >
            <X size={14} /> Close quiz
          </button>
        )}
        <QuizReview questions={questions} category={category} answers={answers} />
      </motion.div>
    );
  }

  return (
    <div data-testid="student-quiz-session">
      <div className="flex items-center justify-between mb-4 max-w-4xl mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/50">
          / {category} · {idx + 1} of {total}
        </div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-md text-white text-sm font-mono">
          <Timer size={13} /> {fmt(timer)}
        </div>
      </div>
      <div className="h-1 w-full max-w-4xl mx-auto bg-white/10 rounded mb-6 overflow-hidden">
        <motion.div className="h-full bg-gradient-to-r from-[#FFD600] via-[#FF7AD9] to-[#7C3AED]"
          initial={{ width: 0 }} animate={{ width: `${((idx + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
      </div>
      <div className="max-w-4xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2 }}>
            {qKind(q, category) === "mcq" && <InlineMCQ q={q} value={answers[idx]} onChange={(v) => setAnswers((a) => ({ ...a, [idx]: v }))} />}
            {qKind(q, category) === "drag_drop" && <InlineDragDrop q={q} value={answers[idx]} onChange={(v) => setAnswers((a) => ({ ...a, [idx]: v }))} />}
            {qKind(q, category) === "fill_blank" && <InlineFillBlank q={q} value={answers[idx]} onChange={(v) => setAnswers((a) => ({ ...a, [idx]: v }))} />}
          </motion.div>
        </AnimatePresence>
        <div className="mt-8 flex items-center justify-between">
          <button onClick={() => idx > 0 && setIdx(idx - 1)} disabled={idx === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm disabled:opacity-30"
            data-testid="student-q-prev"
          ><ChevronLeft size={14} /> Previous</button>
          {idx < total - 1 ? (
            <button onClick={() => setIdx(idx + 1)} data-testid="student-q-next"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-[#0A0C10] text-sm font-semibold hover:scale-[1.02] transition-transform"
            >Next <ChevronRight size={14} /></button>
          ) : (
            <button onClick={finish} data-testid="student-q-finish"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-bold hover:bg-[#FFC600] transition-colors"
            >Submit answers <Send size={14} /></button>
          )}
        </div>
      </div>
    </div>
  );
}

function TeacherLiveResults({ classId, quizId, title, category, total, questions = [] }) {
  const [responses, setResponses] = useState([]);
  const [expanded, setExpanded] = useState("");
  const [chimeMuted, toggleChime] = useChimeMute();
  useAnswerChime(responses, quizId);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/quiz-responses`, { params: { quiz_id: quizId } });
        if (!stop) setResponses(data.responses || []);
      } catch { /* keep polling */ }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { stop = true; clearInterval(t); };
  }, [classId, quizId]);

  const finalCount = responses.filter((r) => r.status === "final").length;
  const avg = responses.length ? Math.round((responses.reduce((s, r) => s + (r.score || 0), 0) / responses.length / Math.max(total, 1)) * 100) : 0;
  return (
    <div className="max-w-4xl mx-auto" data-testid="teacher-live-results">
      <div className="text-center mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold">/ live broadcasting · {category}</div>
        <h2 className="font-display font-bold text-4xl text-white tracking-tighter mt-2">{title}</h2>
        <p className="text-white/60 mt-2 text-sm">Students see this quiz right now. Click a student to see their live answers.</p>
        <button
          onClick={toggleChime}
          data-testid="live-results-chime-toggle"
          className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        >
          {chimeMuted ? <BellOff size={12} /> : <Bell size={12} />}
          {chimeMuted ? "Answer chime off" : "Answer chime on"}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Started" value={responses.length} icon={Users} color="#0EA5E9" />
        <Stat label="Submitted" value={`${finalCount}/${responses.length || 0}`} icon={CheckCircle2} color="#10B981" />
        <Stat label="Avg score" value={`${avg}%`} icon={Trophy} color="#FFD600" />
      </div>
      <div className="space-y-2">
        {responses.length === 0 ? (
          <div className="text-center text-white/40 py-12 border border-dashed border-white/15 rounded-md">
            <Hand size={20} className="mx-auto mb-3 text-white/30" />
            <div className="text-sm">Waiting for students to start…</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-white/30 mt-1">Polling every 2.5s</div>
          </div>
        ) : responses.map((r) => {
          const isOpen = expanded === r.student_id;
          const answeredCount = Object.keys(r.answers || {}).length;
          return (
            <div key={r.student_id} className={`bg-white/[0.04] border rounded-md transition-colors ${isOpen ? "border-[#FFD600]/50" : "border-white/10"}`} data-testid={`resp-${r.student_id}`}>
              <button
                onClick={() => setExpanded(isOpen ? "" : r.student_id)}
                data-testid={`resp-expand-${r.student_id}`}
                className="w-full p-3 flex items-center gap-3 text-left"
              >
                <div className="shrink-0 w-9 h-9 rounded-full bg-[#FFD600]/20 text-[#FFD600] inline-flex items-center justify-center font-bold text-xs">
                  {(r.student_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{r.student_name}</div>
                  <div className="text-[11px] text-white/50 font-mono">
                    {r.status === "final" ? "✓ Submitted" : `On Q${(r.current_q || 0) + 1} · ${answeredCount} answered`} · {fmt(r.time_s || 0)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-display font-bold text-white">{r.score}<span className="text-white/40 text-base">/{r.total}</span></div>
                </div>
                <ChevronDown size={15} className={`text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-3 pb-3" data-testid={`resp-detail-${r.student_id}`}>
                  <StudentAnswerDetail questions={questions} category={category} answers={r.answers || {}} currentQ={r.status === "final" ? -1 : (r.current_q || 0)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Per-question breakdown of ONE student's live answers — used by both the
   quiz results and the Champ Student game results. */
function StudentAnswerDetail({ questions, category, answers, currentQ }) {
  return (
    <div className="border-t border-white/10 pt-3 space-y-1.5">
      {questions.map((q, i) => {
        const a = answers[i];
        const answered = a !== undefined && a !== null;
        const k = qKind(q, category);
        let text = "";
        let ok = null;
        if (answered) {
          if (k === "mcq") {
            text = q.options?.[a] ?? String(a);
            ok = a === correctIndexOf(q);
          } else if (k === "fill_blank") {
            text = Object.values(a || {}).filter(Boolean).join(", ") || "…";
            ok = isCorrectAnswer(k, q, a);
          } else if (k === "drag_drop") {
            text = ddPairs(q, a) || "Nothing placed";
            ok = isCorrectAnswer(k, q, a);
          } else {
            text = String(a);
          }
        }
        const isCurrent = i === currentQ;
        return (
          <div key={i} className={`text-xs p-2.5 rounded-md ${isCurrent ? "bg-[#FFD600]/10 border border-[#FFD600]/30" : "bg-white/[0.03]"}`}>
            <div className="text-white/60 line-clamp-1">Q{i + 1}. {q.question || q.sentence || ""}</div>
            <div className="mt-1 flex items-center gap-2">
              {isCurrent && <span className="text-[#FFD600] font-mono text-[9px] uppercase tracking-wider inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#FFD600] animate-pulse" />viewing now</span>}
              {answered ? (
                <span className={`font-semibold ${ok === true ? "text-emerald-400" : ok === false ? "text-red-400" : "text-white"}`}>
                  {ok === true ? "✓ " : ok === false ? "✗ " : ""}{text}
                </span>
              ) : !isCurrent ? (
                <span className="text-white/30">No answer yet</span>
              ) : null}
            </div>
            {answered && ok === false && (
              <div className="text-[11px] text-white/50 mt-0.5">Correct: <span className="text-emerald-400 font-semibold">{fmtCorrectFor(q, category)}</span></div>
            )}
          </div>
        );
      })}
    </div>
  );
}
function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-md p-3">
      <div className="flex items-center gap-2">
        <Icon size={14} style={{ color }} />
        <div className="text-[10px] font-mono uppercase tracking-wider text-white/50">{label}</div>
      </div>
      <div className="font-display font-bold text-3xl text-white tracking-tighter mt-1">{value}</div>
    </div>
  );
}

/* ───────────────────── inline quiz players (compact, dark theme) ───────────────────── */
function InlineMCQ({ q, value, onChange }) {
  const L = ["A", "B", "C", "D"];
  return (
    <div className="bg-white rounded-2xl p-8" data-testid="inline-mcq">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#7C3AED] font-bold mb-3">Question</div>
      <h3 className="font-display font-bold text-2xl text-[#0A0C10] tracking-tighter leading-tight">{q.question}</h3>
      <div className="grid sm:grid-cols-2 gap-2.5 mt-6">
        {(q.options || []).slice(0, 4).map((opt, i) => {
          const active = value === i;
          return (
            <button key={i} onClick={() => onChange(i)} data-testid={`inline-mcq-${i}`}
              className={`flex items-center gap-3 p-3 rounded-lg text-left border-2 transition-colors ${active ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "border-neutral-200 hover:border-[#7C3AED]"}`}
            >
              <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${active ? "bg-white text-[#7C3AED]" : "bg-[#7C3AED]/10 text-[#7C3AED]"}`}>{L[i]}</div>
              <div className={`text-sm font-medium ${active ? "text-white" : "text-[#0A0C10]"}`}>{opt}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
function InlineFillBlank({ q, value, onChange }) {
  const sentence = q.sentence || q.question || q.q || "";
  const parts = useMemo(() => {
    const re = /\{\{(\d+)\}\}/g, result = [];
    let last = 0, m;
    while ((m = re.exec(sentence)) != null) {
      if (m.index > last) result.push({ type: "text", text: sentence.slice(last, m.index) });
      result.push({ type: "blank", idx: Number(m[1]) });
      last = m.index + m[0].length;
    }
    if (last < sentence.length) result.push({ type: "text", text: sentence.slice(last) });
    // No {{n}} placeholders (e.g. "The founder was ______.") → one typed answer
    if (!result.some((p) => p.type === "blank")) result.push({ type: "text", text: "  " }, { type: "blank", idx: 0 });
    return result;
  }, [sentence]);
  const update = (i, v) => onChange({ ...(value || {}), [i]: v });
  return (
    <div className="bg-white rounded-2xl p-8" data-testid="inline-fb">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#10B981] font-bold mb-4">Fill in the blank</div>
      <div className="font-display font-bold text-3xl text-[#0A0C10] tracking-tighter leading-snug">
        {parts.map((p, i) => p.type === "text" ? <span key={i}>{p.text}</span> : (
          <input key={i} type="text" value={value?.[p.idx] || ""} onChange={(e) => update(p.idx, e.target.value)}
            data-testid={`inline-fb-${p.idx}`}
            className="inline-block mx-1 px-3 py-1 min-w-[120px] border-b-4 border-[#10B981] bg-[#10B981]/5 focus:bg-[#10B981]/10 focus:outline-none text-[#10B981] text-center font-semibold text-2xl rounded-md"
            placeholder="…"
            style={{ width: `${Math.max((value?.[p.idx] || "").length + 4, 8)}ch` }} />
        ))}
      </div>
    </div>
  );
}
function InlineDragDrop({ q, value, onChange }) {
  const [dragging, setDragging] = useState(null);
  const placements = value || {};
  const items = q.items || [], zones = q.zones || [];
  const placedOn = (zid) => items.filter((it) => placements[it.id] === zid);
  const unplaced = items.filter((it) => !placements[it.id]);
  const drop = (zid) => { if (!dragging) return; const n = { ...placements }; delete n[dragging]; if (zid) n[dragging] = zid; onChange(n); setDragging(null); };
  return (
    <div className="bg-white rounded-2xl p-6" data-testid="inline-dd">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#0EA5E9] font-bold mb-3">Drag & Drop</div>
      <h3 className="font-display font-bold text-2xl text-[#0A0C10] tracking-tighter mb-5">{q.question}</h3>
      <div className="bg-[#FBFAF7] rounded-xl p-3 border-2 border-dashed border-neutral-200 mb-4" onDragOver={(e) => e.preventDefault()} onDrop={() => drop(null)}>
        <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">Drag from here</div>
        <div className="flex flex-wrap gap-2">
          {unplaced.length === 0 ? <div className="text-xs text-neutral-400 italic">All placed.</div> : unplaced.map((it) => (
            <div key={it.id} draggable onDragStart={() => setDragging(it.id)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0A0C10] text-white font-semibold text-sm cursor-grab"><Hand size={12} /> {it.label}</div>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {zones.map((z) => (
          <div key={z.id} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(z.id)} className="bg-[#0EA5E9]/5 border-2 border-dashed border-[#0EA5E9]/40 rounded-xl p-3 min-h-[90px]">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#0EA5E9] font-bold mb-2">{z.label}</div>
            <div className="flex flex-wrap gap-2">
              {placedOn(z.id).map((it) => (
                <div key={it.id} draggable onDragStart={() => setDragging(it.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0EA5E9] text-white font-semibold text-sm cursor-grab"><Hand size={12} /> {it.label}</div>
              ))}
              {placedOn(z.id).length === 0 && <div className="text-xs text-neutral-400 italic">Drop here…</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────── resource viewer ───────────────────── */
function ResourceViewer({ payload }) {
  const url = payload?.file_url || "";
  const kind = (payload?.kind || "").toLowerCase();
  const isImage = kind === "image" || /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url) || url.startsWith("data:image/");

  if (isImage) {
    return (
      <div className="bg-white rounded-2xl p-4 max-w-3xl mx-auto" data-testid="live-resource-viewer">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#A855F7] font-bold">/ shared image</div>
          <div className="flex-1" />
          <a href={url} target="_blank" rel="noreferrer" className="text-[11px] font-mono uppercase tracking-wider text-[#0A0C10]/60 hover:text-[#0A0C10] inline-flex items-center gap-1">
            <ExternalLink size={11} /> open
          </a>
        </div>
        {payload?.title && (
          <h3 className="font-display font-bold text-xl text-[#0A0C10] tracking-tighter mb-3">{payload.title}</h3>
        )}
        <img src={url} alt={payload?.title || "shared"} className="w-full h-auto rounded-md" data-testid="live-resource-image" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-8 text-center max-w-2xl mx-auto" data-testid="live-resource-viewer">
      <FileText size={36} className="mx-auto text-[#A855F7] mb-4" />
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#A855F7] font-bold">/ shared resource</div>
      <h3 className="font-display font-bold text-2xl text-[#0A0C10] tracking-tighter mt-2">{payload?.title}</h3>
      <p className="text-sm text-neutral-500 mt-2">Open the file in a new tab to view.</p>
      <a href={pageUrl(url)} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0A0C10] text-white text-sm font-medium hover:bg-[#0A0C10]/90 transition-colors">
        <ExternalLink size={14} /> Open file
      </a>
    </div>
  );
}

function EmptyShell({ icon: Icon, msg }) {
  return (
    <div className="text-center py-20 text-white/50">
      <Icon size={32} className="mx-auto mb-3 text-white/30" />
      <div className="text-sm">{msg}</div>
    </div>
  );
}

/* ───────────────────── helpers ───────────────────── */
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/* Per-QUESTION type detection — quizzes can mix MCQ / fill-blank / drag-drop
   questions (or carry a wrong category on the doc), so never trust the
   quiz-level category alone. */
function qKind(q, fallback = "mcq") {
  if (!q) return fallback;
  if (q.sentence) return "fill_blank";
  if (q.items && q.zones) return "drag_drop";
  if (q.options?.length) return "mcq";
  if (q.type === "fill_blank" || q.type === "short" || q.type === "short_answer") return "fill_blank";
  if (q.question || q.q) return "fill_blank"; // text question without options → typed answer
  return fallback;
}

/* Quiz question docs are inconsistent across generators — resolve the
   correct MCQ index via the shared helper. */

function scoreFn(category, questions, answers) {
  let correct = 0;
  questions.forEach((qq, i) => {
    if (isCorrectAnswer(qKind(qq, category), qq, answers[i])) correct += 1;
  });
  return correct;
}

function isCorrectAnswer(category, qq, a) {
  if (a == null) return false;
  category = qKind(qq, category);
  if (category === "mcq") return a === correctIndexOf(qq);
  if (category === "fill_blank") {
    const ans = (qq.answers && qq.answers.length) ? qq.answers : [qq.answer || qq.correct_answer || ""];
    return ans.some((x) => (x || "").trim()) && ans.every((expected, bi) => lenientMatch(a?.[bi] || "", expected || ""));
  }
  if (category === "drag_drop") {
    return !!(a && qq.correct) && Object.entries(qq.correct).every(([itemId, zoneId]) => a[itemId] === zoneId);
  }
  return false;
}

/* Drag-drop mapping → human-readable "Item → Zone" pairs. */
function ddPairs(qq, mapping) {
  const zoneLabel = (zid) => qq.zones?.find((z) => z.id === zid)?.label || zid;
  const itemLabel = (iid) => qq.items?.find((it) => it.id === iid)?.label || iid;
  const pairs = Object.entries(mapping || {}).map(([iid, zid]) => `${itemLabel(iid)} → ${zoneLabel(zid)}`);
  return pairs.join(" · ");
}

/* The correct answer of a question as display text — shared by student review
   and the teacher's per-student breakdown. */
function fmtCorrectFor(qq, category) {
  const k = qKind(qq, category);
  if (k === "mcq") {
    const i = correctIndexOf(qq);
    return qq.options?.[i] ?? String(qq.answer ?? qq.correct_answer ?? "");
  }
  if (k === "fill_blank") return ((qq.answers && qq.answers.length) ? qq.answers : [qq.answer || qq.correct_answer || ""]).filter(Boolean).join(", ");
  if (k === "drag_drop") return ddPairs(qq, qq.correct) || String(qq.answer ?? qq.correct_answer ?? "");
  return "";
}

/* Per-question review shown to the student after submitting — which answers
   were right/wrong, and what the correct answer was. */
function QuizReview({ questions, category, answers }) {
  const fmtAnswer = (qq, a) => {
    const k = qKind(qq, category);
    if (a == null) return "—";
    if (k === "mcq") return qq.options?.[a] ?? String(a);
    if (k === "fill_blank") return Object.values(a || {}).filter(Boolean).join(", ") || "—";
    if (k === "drag_drop") return ddPairs(qq, a) || "Nothing placed";
    return String(a);
  };
  const fmtCorrect = (qq) => fmtCorrectFor(qq, category);
  return (
    <div className="mt-8 max-w-2xl mx-auto text-left space-y-2" data-testid="quiz-review">
      <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-white/50 text-center mb-3">/ your answers reviewed</div>
      {questions.map((qq, i) => {
        const a = answers[i];
        const ok = isCorrectAnswer(category, qq, a);
        return (
          <div key={i} className={`p-3.5 rounded-lg border ${ok ? "border-emerald-400/30 bg-emerald-500/[0.07]" : "border-red-400/30 bg-red-500/[0.07]"}`} data-testid={`quiz-review-q${i}`}>
            <div className="text-white/80 text-sm font-medium">Q{i + 1}. {qq.question || qq.sentence || ""}</div>
            <div className="mt-1.5 flex items-start gap-2 text-sm">
              {ok ? <CheckCircle2 size={15} className="text-emerald-400 mt-0.5 shrink-0" /> : <X size={15} className="text-red-400 mt-0.5 shrink-0" />}
              <div>
                <span className={ok ? "text-emerald-300 font-semibold" : "text-red-300 font-semibold"}>{fmtAnswer(qq, a)}</span>
                {!ok && (
                  <span className="text-white/60 block text-xs mt-0.5">Correct: <span className="text-emerald-300 font-semibold">{fmtCorrect(qq)}</span></span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}



/* ───────────────────── millionaire (Champ Student) live viewer ───────────────────── */
function MillionaireViewer({ classId, gameId, payload, isTeacher, guestToken, guestName, isRemote, canInteract, allowedNames, myName, onStudentClose }) {
  const { questions = [], title = "Champ Student" } = payload || {};

  // REMOTE ACCESS — one shared run: chosen participant(s) play, everyone
  // else (incl. teacher) watches the live mirror. Teacher hands control via
  // the "change" chip in the overlay header.
  if (isRemote) {
    if (!isTeacher && canInteract) {
      return (
        <StudentChampSession classId={classId} gameId={gameId} title={title} questions={questions} guestToken={guestToken} guestName={guestName} myName={myName} onStudentClose={onStudentClose} remote initialState={payload?.session} />
      );
    }
    return <ChampMirrorStage questions={questions} title={title} session={payload?.session || {}} playerLabel={payload?.session?.player || allowedNames.join(", ") || "Player"} />;
  }

  if (!isTeacher) {
    return (
      <StudentChampSession classId={classId} gameId={gameId} title={title} questions={questions} guestToken={guestToken} guestName={guestName} myName={myName} onStudentClose={onStudentClose} />
    );
  }
  return <TeacherChampResults classId={classId} gameId={gameId} title={title} total={questions.length} questions={questions} />;
}

function StudentChampSession({ classId, gameId, title, questions, guestToken, guestName, myName, onStudentClose, remote = false, initialState }) {
  const [done, setDone] = useState(false);
  const playerName = guestName || myName || "Student";

  const postResponse = (body) => {
    if (guestToken) {
      return api.post(`/public/class/${classId}/quiz-response`, {
        ...body, token: guestToken, guest_name: playerName,
      });
    }
    return api.post(`/live/${classId}/quiz-response`, body);
  };
  // Live progress stream — teacher dashboards + (remote mode) the shared mirror.
  const report = (s, status) => {
    postResponse({
      quiz_id: gameId, answers: s.answers || {}, score: s.cleared || 0,
      total: questions.length, time_s: 0, current_q: s.current_q || 0, status,
    }).catch(() => {});
    if (remote) {
      const body = { payload: { session: { ...s, status, player: playerName } } };
      (guestToken
        ? api.post(`/public/class/${classId}/session-state`, { token: guestToken, guest_name: playerName, ...body })
        : api.post(`/live/${classId}/session-state`, body)
      ).catch(() => {});
    }
  };

  const handleFinish = (finalState) => {
    report({ ...finalState, current_q: questions.length - 1 }, "final");
    if (!guestToken) {
      api.post("/badges/award", {
        quiz_id: gameId, score: finalState.cleared || 0,
        total: questions.length, context: "live",
      }).catch(() => {});
    }
  };

  if (done) {
    return (
      <div className="text-center py-20 text-white" data-testid="student-champ-done">
        <Crown size={48} className="text-[#FFD600] mx-auto mb-4" />
        <h2 className="font-display font-bold text-4xl tracking-tighter">Submitted!</h2>
        <p className="text-white/60 mt-2">Your teacher sees your run now.</p>
        {onStudentClose && (
          <button
            onClick={onStudentClose}
            data-testid="student-champ-close"
            className="mt-8 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-white text-[#0A0C10] text-sm font-bold hover:bg-neutral-100 transition-colors"
          >
            <X size={14} /> Close game
          </button>
        )}
      </div>
    );
  }
  return (
    <ChampStudentPlayer
      game={{ id: gameId, title, questions }}
      playerName={playerName}
      initialState={remote ? initialState : undefined}
      onProgress={(s) => report(s, "playing")}
      onFinish={handleFinish}
      onClose={() => setDone(true)}
    />
  );
}

function TeacherChampResults({ classId, gameId, title, total, questions = [] }) {
  const [responses, setResponses] = useState([]);
  const [expanded, setExpanded] = useState("");
  useAnswerChime(responses, gameId);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/quiz-responses`, { params: { quiz_id: gameId } });
        if (!stop) setResponses(data.responses || []);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { stop = true; clearInterval(t); };
  }, [classId, gameId]);

  return (
    <div className="max-w-4xl mx-auto" data-testid="teacher-champ-results">
      <div className="text-center mb-6">
        <Crown size={36} className="text-[#FFD600] mx-auto mb-2" />
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold">/ champ student · LIVE</div>
        <h2 className="font-display font-bold text-4xl text-white tracking-tighter mt-2">{title}</h2>
        <p className="text-white/60 mt-2 text-sm">Students are playing now — click a student to watch their picks live.</p>
      </div>
      <div className="space-y-2">
        {responses.length === 0 ? (
          <div className="text-center text-white/40 py-12 border border-dashed border-white/15 rounded-md">
            <Hand size={20} className="mx-auto mb-3 text-white/30" />
            <div className="text-sm">Waiting for students to start the show…</div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-white/30 mt-1">Polling every 2.5s</div>
          </div>
        ) : responses.map((r) => {
          const isOpen = expanded === r.student_id;
          return (
            <div key={r.student_id} className={`bg-white/[0.04] border rounded-md transition-colors ${isOpen ? "border-[#FFD600]/50" : "border-white/10"}`} data-testid={`champ-resp-${r.student_id}`}>
              <button
                onClick={() => setExpanded(isOpen ? "" : r.student_id)}
                data-testid={`champ-expand-${r.student_id}`}
                className="w-full p-3 flex items-center gap-3 text-left"
              >
                <div className="shrink-0 w-9 h-9 rounded-full bg-[#FFD600]/20 text-[#FFD600] inline-flex items-center justify-center font-bold text-xs">
                  {(r.student_name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{r.student_name}</div>
                  <div className="text-[11px] text-white/50 font-mono">
                    {r.status === "final" ? "✓ Finished" : `Playing · on Q${(r.current_q || 0) + 1}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-display font-bold text-white">{r.score}<span className="text-white/40 text-base">/{total}</span></div>
                </div>
                <ChevronDown size={15} className={`text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-3 pb-3" data-testid={`champ-detail-${r.student_id}`}>
                  <StudentAnswerDetail questions={questions} category="mcq" answers={r.answers || {}} currentQ={r.status === "final" ? -1 : (r.current_q || 0)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────── slides live viewer (student — follows the teacher) ─────────────
   The current slide index, theme, images and shapes all come from the overlay
   payload, which the teacher updates live via /overlay-update. */
function SlidesViewer({ payload, canControl = false, classId, guestToken, guestName }) {
  const { slides = [], title = "Slides", theme = "indigo", current_index = 0 } = payload || {};
  const [localIdx, setLocalIdx] = useState(null);
  const lastNavRef = useRef(0);
  if (slides.length === 0) return <EmptyShell icon={FileText} msg="Empty deck." />;
  const serverIdx = Math.max(0, Math.min(current_index, slides.length - 1));
  // Controller keeps an optimistic index until the poll echoes it back
  const idx = (canControl && localIdx != null && Date.now() - lastNavRef.current < 3000) ? localIdx : serverIdx;
  const nav = (n) => {
    if (!canControl) return;
    const clamped = Math.max(0, Math.min(n, slides.length - 1));
    lastNavRef.current = Date.now();
    setLocalIdx(clamped);
    const body = { payload: { current_index: clamped } };
    (guestToken
      ? api.post(`/public/class/${classId}/session-state`, { token: guestToken, guest_name: guestName || "Guest", ...body })
      : api.post(`/live/${classId}/session-state`, body)
    ).catch(() => {});
  };
  const slide = slides[idx] || {};
  const th = themeFor(theme);
  return (
    <div className="w-full max-w-[1600px] mx-auto" data-testid="live-slides-viewer">
      <div className="text-center text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold mb-3">
        / slides · {idx + 1} / {slides.length}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
          className={`relative bg-gradient-to-br ${th.bg} ${th.text} rounded-2xl p-10 lg:p-14 min-h-[calc(100vh-14rem)] flex flex-col justify-center overflow-hidden border`}
          style={{ borderColor: th.accent + "44" }}
          data-testid={`live-slide-${idx}`}
        >
          {/* Decorative shape overlays — mirror the teacher's toolbox picks */}
          {slide.shape === "dot" && (
            <div className="absolute inset-0 pointer-events-none opacity-30" style={{
              backgroundImage: `radial-gradient(${th.accent} 1px, transparent 1.5px)`, backgroundSize: "22px 22px",
            }} />
          )}
          {slide.shape === "arrow" && (
            <svg className="absolute right-6 bottom-6 pointer-events-none" width="140" height="80" viewBox="0 0 140 80" fill="none">
              <path d="M10 40 L 120 40 M 100 20 L 120 40 L 100 60" stroke={th.accent} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
            </svg>
          )}
          {slide.shape === "corner" && (
            <>
              <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 pointer-events-none" style={{ borderColor: th.accent }} />
              <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 pointer-events-none" style={{ borderColor: th.accent }} />
            </>
          )}
          {slide.shape === "bar" && (
            <div className="absolute left-0 top-0 bottom-0 w-2 pointer-events-none" style={{ background: th.accent }} />
          )}

          <div className="relative z-10 grid gap-8 items-start" style={{ gridTemplateColumns: slide.image_url ? "1.4fr 1fr" : "1fr" }}>
            <div>
              <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tighter leading-[1.05]">{slide.title}</h2>
              {(slide.body || []).length > 0 && (
                <ul className="mt-8 space-y-3">
                  {(slide.body || []).map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-lg">
                      <Sparkles size={14} className="mt-2 shrink-0" style={{ color: th.accent }} /><span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {slide.image_url && (
              <div className="rounded-md overflow-hidden border" style={{ borderColor: th.accent + "55" }}>
                <img src={slide.image_url} alt={slide.title} className="w-full h-auto object-contain bg-black/30" />
              </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="mt-4 text-center">
        {canControl ? (
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => nav(idx - 1)} disabled={idx === 0} data-testid="shared-slides-prev"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white text-sm disabled:opacity-30">
              <ChevronLeft size={14} /> Previous
            </button>
            <span className="inline-flex items-center gap-2 text-[10px] text-emerald-400 font-mono uppercase tracking-[0.18em]">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> You control — everyone follows
            </span>
            <button onClick={() => nav(idx + 1)} disabled={idx >= slides.length - 1} data-testid="shared-slides-next"
              className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#FFD600] text-[#0A0C10] text-sm font-semibold disabled:opacity-30">
              Next <ChevronRight size={14} />
            </button>
          </div>
        ) : (
          <span className="inline-flex items-center gap-2 text-xs text-white/50 font-mono uppercase tracking-[0.18em]">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Teacher is presenting — slides follow automatically
          </span>
        )}
        <div className="text-xs text-white/40 mt-1">{title}</div>
      </div>
    </div>
  );
}
